import React, { useState, useEffect } from "react";
import { subscriptionService } from "../../services/enhancedCrudService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const MySubscription = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await subscriptionService.getCurrentSellerSubscription();
      setSubscription(response);
    } catch (err) {
      console.error("Error fetching subscription:", err);
      setRedirecting(true);
      toast.error("No subscription found. Taking you to available plans...");
      sessionStorage.clear();
      setTimeout(() => {
        navigate("/subscription");
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Asia/Kolkata"
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  const formatCurrency = (amount, currency = "INR") => {
    if (!amount) return "N/A";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return "N/A";
    try {
      const end = new Date(endDate);
      const now = new Date();
      const diffTime = end - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) {
        return "Expired";
      } else if (diffDays === 0) {
        return "Expires today";
      } else {
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} remaining`;
      }
    } catch (error) {
      console.error("Error calculating days remaining:", error);
      return "N/A";
    }
  };

  if (loading || redirecting) {
    return (
      <div className="page-header">
        <div className="row">
          <div className="col-sm-12">
            <h3 className="page-title">
              <i className="fe fe-credit-card me-2" style={{color: '#006666'}}></i>
              My Subscription
            </h3>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-5">
                <div className="loading-spinner">
                  <div className="spinner-border text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}>
                    <span className="sr-only">Loading...</span>
                  </div>
                  <h5 className="text-muted">
                    {redirecting ? "No subscription found..." : "Loading subscription details..."}
                  </h5>
                  <p className="text-muted mb-0">
                    {redirecting 
                      ? "Taking you to available plans..." 
                      : "Please wait while we fetch your subscription information"
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }


  if (!subscription) {
    return (
      <div className="page-header">
        <div className="row">
          <div className="col-sm-12">
            <h3 className="page-title">
              <i className="fe fe-credit-card me-2" style={{color: '#006666'}}></i>
              My Subscription
            </h3>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-5">
                <div className="no-subscription-state">
                  <div className="no-subscription-icon mb-4">
                    <i className="fe fe-package text-info" style={{fontSize: '4rem'}}></i>
                  </div>
                  <h4 className="text-info mb-3">No Active Subscription</h4>
                  <p className="text-muted mb-4">
                    You don't have an active subscription. Please subscribe to a plan to access all premium features.
                  </p>
                  <a href="/subscription" className="btn btn-primary btn-lg">
                    <i className="fe fe-credit-card mr-2" style={{color: '#006666'}}></i>
                    View Available Plans
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-header">
      <div className="row">
        <div className="col-sm-12">
          <h3 className="page-title mb-3">
            <i className="fe fe-credit-card me-2" style={{color: '#006666'}}></i>
            My Subscription
          </h3>
        </div>
      </div>

      <div className="row">
        {/* Subscription Details */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-bottom-0">
              <div className="d-flex align-items-center">
                <div className="icon-circle me-3" style={{backgroundColor: '#006666'}}>
                  <i className="fe fe-info text-white"></i>
                </div>
                <div>
                  <h5 className="mb-0">Subscription Details</h5>
                  <p className="text-muted mb-0">Your subscription information</p>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-4">
                  <div className="info-item">
                    <label className="info-label">
                      <i className="fe fe-calendar me-2" style={{color: '#006666'}}></i>
                      Start Date
                    </label>
                    <div className="info-value">{formatDate(subscription.start_date)}</div>
                  </div>
                </div>
                <div className="col-md-6 mb-4">
                  <div className="info-item">
                    <label className="info-label">
                      <i className="fe fe-calendar me-2" style={{color: '#006666'}}></i>
                      End Date
                    </label>
                    <div className="info-value">
                      {formatDate(subscription.end_date)}
                      <div className="text-muted small mt-1">
                        <i className="fe fe-clock mr-1"></i>
                        {getDaysRemaining(subscription.end_date)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {subscription.transaction_id && (
                <div className="row">
                  <div className="col-md-6 mb-4">
                    <div className="info-item">
                      <label className="info-label">
                        <i className="fe fe-credit-card me-2" style={{color: '#006666'}}></i>
                        Transaction ID
                      </label>
                      <div className="info-value">
                        <code className="bg-light px-2 py-1 rounded">{subscription.transaction_id}</code>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Plan Details */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-bottom-0">
              <div className="d-flex align-items-center">
                <div className="icon-circle me-3" style={{backgroundColor: '#006666'}}>
                  <i className="fe fe-package text-white"></i>
                </div>
                <div>
                  <h5 className="mb-0">Plan Details</h5>
                  <p className="text-muted mb-0">Your current plan</p>
                </div>
              </div>
            </div>
            <div className="card-body">
              {subscription.plan ? (
                <>
                  <div className="plan-card">
                    <div className="plan-header">
                      <h4 className="plan-name">{subscription.plan.name}</h4>
                      <div className="plan-price">
                        {formatCurrency(subscription.plan.price, subscription.plan.currency)}
                      </div>
                    </div>
                    
                    <div className="plan-details">
                      <div className="plan-detail-item">
                        <i className="fe fe-clock me-2" style={{color: '#006666'}}></i>
                        <span>{subscription.plan.duration_months} month{subscription.plan.duration_months !== 1 ? 's' : ''}</span>
                      </div>
                      
                      {subscription.plan.description && (
                        <div className="plan-detail-item">
                          <i className="fe fe-info me-2" style={{color: '#006666'}}></i>
                          <span>{subscription.plan.description}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="fe fe-package mb-2" style={{fontSize: '2rem', color: '#006666'}}></i>
                  <p>No plan details available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .icon-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }
        
        .info-item {
          margin-bottom: 1rem;
        }
        
        .info-label {
          font-weight: 600;
          color: #6c757d;
          font-size: 0.875rem;
          margin-bottom: 0.5rem;
          display: block;
        }
        
        .info-value {
          font-size: 1rem;
          color: #212529;
          font-weight: 500;
        }
        
        .plan-card {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-radius: 12px;
          padding: 1.5rem;
          border: 1px solid #dee2e6;
        }
        
        .plan-header {
          text-align: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #dee2e6;
        }
        
        .plan-name {
          font-size: 1.25rem;
          font-weight: 700;
          color: #212529;
          margin-bottom: 0.5rem;
        }
        
        .plan-price {
          font-size: 1.5rem;
          font-weight: 700;
          color: #28a745;
        }
        
        .plan-details {
          margin-bottom: 1rem;
        }
        
        .plan-detail-item {
          display: flex;
          align-items: center;
          margin-bottom: 0.75rem;
          font-size: 0.9rem;
          color: #6c757d;
        }
        
        .roles-container {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        
        .role-badge {
          background: #007bff;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        .badge-outline-primary {
          background: transparent;
          border: 1px solid #007bff;
          color: #007bff;
        }
        
        .subscription-status-badge .badge {
          font-size: 0.9rem;
          padding: 0.5rem 1rem;
        }
        
        .bg-gradient-primary {
          background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
        }
        
        .card {
          transition: transform 0.2s ease-in-out;
        }
        
        .card:hover {
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
};

export default MySubscription;
